import { useSelector } from 'react-redux';
import { CartoLayer } from '@deck.gl/carto';
import { useCartoLayerProps } from '@carto/react-api';
import { CompositeLayer } from '@deck.gl/core';
import countyBroadbandSource from 'data/sources/countyBroadbandSource';
import { broadbandColor } from 'utils/broadbandScores';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
// import { setViewState } from '@carto/react-redux';
import ReactDOMServer from 'react-dom/server';
import { generateCountyId } from '../../utils/countyId';
import Popover from '../common/Popover';

export const COUNTY_BROADBAND_LAYER_ID = 'countyBroadbandLayer';
export const COUNTY_BROADBAND_LAYER_HOVER_ID = 'countyBroadbandLayerHover';
export const ACTIVE_COUNTY_BROADBAND_LAYER_ID = 'activeCountyBroadbandLayer';

const ACTIVE_COMPARE_COUNTY_A_COLOR = [99, 196, 89];
const ACTIVE_COMPARE_COUNTY_B_COLOR = [210, 104, 208];

export default function CountyBroadbandLayer() {
  const [layer, setLayer] = useState(null);
  const [previousActiveCounty, setPreviousActiveCounty] = useState(null);
  const [previousActiveCompareCounty, setPreviousActiveCompareCounty] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousHoveredCountyId, setPreviousHoveredCountyId] = useState(null);
  const [previousYear, setPreviousYear] = useState(null);
  const [previousActivePanel, setPreviousActivePanel] = useState(null);
  const [previousActive, setPreviousActive] = useState(null);
  const [previousBroadbandMin, setPreviousBroadbandMin] = useState(null);
  const [previousBroadbandMax, setPreviousBroadbandMax] = useState(null);
  const { countyBroadbandLayer } = useSelector((state) => state.carto.layers);
  const source = countyBroadbandSource(countyBroadbandLayer?.year || 2019);
  const cartoLayerProps = useCartoLayerProps({ source });
  // const viewState = useSelector((state) => state.carto.viewState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [hoveredCountyId, setHoveredCountyId] = useState(null);
  const [active, setActive] = useState(false);
  const viewState = useSelector((state) => state.carto.viewState);
  // const dispatch = useDispatch();

  useEffect(() => {
    let activeState = searchParams.get('active-state') || null;
    let activeCounty = searchParams.get('active-county') || null;
    let activeTract = searchParams.get('active-tract') || null;

    setActive(activeState || activeCounty || activeTract);
  }, [active, searchParams, setActive]);

  // const zoomCountyIntoView = useCallback(
  //   ({ lng, lat }) => {
  //     dispatch(
  //       setViewState({
  //         // zoom: 8,
  //         // zoom: 6,
  //         latitude: lat,
  //         longitude: lng,
  //       })
  //     );
  //   },
  //   [dispatch]
  // );

  useEffect(() => {
    if (!countyBroadbandLayer || !source) {
      return;
    }

    if (isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom) === false) {
      setLayer(null);
      return;
    }

    if (
      layer &&
      previousActive === active &&
      previousActiveCounty === searchParams.get('active-county') &&
      previousActiveCompareCounty === searchParams.get('active-compare-county') &&
      previousSocioeconomicFactor === searchParams.get('socioeconomic-factor') &&
      previousHoveredCountyId === hoveredCountyId &&
      previousYear === searchParams.get('year') &&
      previousActivePanel === searchParams.get('active-panel') &&
      previousBroadbandMin === searchParams.get('broadband-min') &&
      previousBroadbandMax === searchParams.get('broadband-max')
    ) {
      return;
    }

    // if (
    //   countyBroadbandLayer?.data?.[0]?.[searchParams.get('socioeconomic-factor')] ===
    //   undefined
    // ) {
    //   return;
    // }

    let mainCountyBroadbandLayer = new CartoLayer({
      ...cartoLayerProps,
      id: COUNTY_BROADBAND_LAYER_ID,
      getFillColor: (object) => {
        let broadbandScore = object.properties.broadband;
        let id = generateCountyId(
          object?.properties?.name,
          object?.properties?.statename
        );
        let isActiveCompareCounty = searchParams.get('active-compare-county') === id;

        if (isActiveCompareCounty === true) {
          return ACTIVE_COMPARE_COUNTY_B_COLOR;
        }

        if (
          searchParams.get('active-panel') === 'compare-counties' &&
          searchParams.get('active-county') === id
        ) {
          return ACTIVE_COMPARE_COUNTY_A_COLOR;
        }

        if (searchParams.get('active-county') === id || hoveredCountyId === id) {
          return broadbandColor(broadbandScore);
        }

        return broadbandColor(
          broadbandScore,
          active !== null ||
            object.properties.broadband < searchParams.get('broadband-min') ||
            object.properties.broadband > searchParams.get('broadband-max')
        );
      },
      pointRadiusMinPixels: 2,
      getLineColor: [255, 255, 255],
      lineWidthMinPixels: 1,
      pickable: true,
      // pickable: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom),
      // opacity: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom) ? 1 : 0,
      updateTriggers: {
        // pickable: [viewState],
        // opacity: [viewState, active],
        getFillColor: [
          active,
          searchParams.get('active-county'),
          searchParams.get('active-compare-county'),
          searchParams.get('year'),
          searchParams.get('broadband-min'),
          searchParams.get('broadband-max'),
          hoveredCountyId,
        ],
      },
    });

    let countyBroadbandHoverLayer = new CartoLayer({
      ...cartoLayerProps,
      id: COUNTY_BROADBAND_LAYER_HOVER_ID,
      getFillColor: [0, 0, 0, 0],
      pointRadiusMinPixels: 2,
      getLineColor: (object) => {
        return hoveredCountyId ===
          generateCountyId(object?.properties?.name, object?.properties?.statename) &&
          searchParams.get('active-county') !== hoveredCountyId &&
          searchParams.get('active-compare-county') !== hoveredCountyId
          ? [6, 49, 93]
          : [255, 255, 255, 0];
      },
      // opacity: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1.5,
      // pickable: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom),
      pickable: true,
      updateTriggers: {
        // opacity: [viewState],
        // pickable: [viewState],
        getLineColor: [
          hoveredCountyId,
          searchParams.get('active-county'),
          searchParams.get('active-compare-county'),
        ],
      },
    });

    let activeCountyBroadbandLayer = new CartoLayer({
      ...cartoLayerProps,
      id: ACTIVE_COUNTY_BROADBAND_LAYER_ID,
      getFillColor: [0, 0, 0, 0],
      pointRadiusMinPixels: 2,
      getLineColor: (object) => {
        if (
          searchParams.get('active-county') ===
            generateCountyId(object?.properties?.name, object?.properties?.statename) ||
          searchParams.get('active-compare-county') ===
            generateCountyId(object?.properties?.name, object?.properties?.statename)
        ) {
          return [6, 49, 93];
        }

        return [255, 255, 255, 0];
      },
      // opacity: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1.5,
      // pickable: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom),
      pickable: true,
      updateTriggers: {
        // pickable: [viewState],
        // opacity: [viewState],
        getLineColor: [
          searchParams.get('active-county'),
          searchParams.get('active-compare-county'),
        ],
      },
    });

    class CountyBroadbandCompositeLayer extends CompositeLayer {
      renderLayers() {
        return [
          mainCountyBroadbandLayer,
          countyBroadbandHoverLayer,
          activeCountyBroadbandLayer,
        ];
      }
    }

    let countyBroadbandCompositeLayer = new CountyBroadbandCompositeLayer({
      onHover: (info) => {
        setHoveredCountyId(
          generateCountyId(
            info.object?.properties?.name,
            info.object?.properties?.statename
          ) || null
        );

        if (info?.object) {
          info.object = {
            html: ReactDOMServer.renderToStaticMarkup(
              <Popover>
                <div className='whitespace-nowrap flex flex-col gap-1'>
                  <div className='flex gap-12 items-center justify-between'>
                    <div>
                      {info.object?.properties?.name}
                      <span className='text-blue-300'>
                        , {info.object?.properties?.statename}
                      </span>
                    </div>
                    <div className='text-blue-300 text-sm'>
                      ({info.object?.properties?.year})
                    </div>
                  </div>
                  <div className='text-sm'>
                    Broadband:{' '}
                    <span className='text-blue-300'>
                      {Math.round(info.object?.properties?.broadband * 100) / 100}
                    </span>
                  </div>
                </div>
              </Popover>
            ),
            style: {
              background: 'transparent',
            },
          };
        }
      },
      onClick: (info) => {
        setSearchParams((params) => {
          params.set(
            'active-county',
            generateCountyId(
              info.object?.properties?.name,
              info.object?.properties?.statename
            ) || null
          );
          params.delete('active-tract');
          params.delete('active-state');
          params.delete('active-panel');
          params.delete('active-compare-county');
          params.delete('active-compare-state');
          params.delete('active-compare-tract');
          params.delete('active-compare-similar-tract');

          return params;
        });

        // zoomCountyIntoView({
        //   lng: info.object.properties.lng,
        //   lat: info.object.properties.lat,
        // });
      },
      updateTriggers: {
        getLineColor: [hoveredCountyId, searchParams.get('active-county')],
        lineWidthMinPixels: [hoveredCountyId],
        // opacity: [viewState],
        // pickable: [viewState],
        getFillColor: [
          active,
          searchParams.get('active-county'),
          searchParams.get('year'),
          searchParams.get('active-compare-county'),
          searchParams.get('active-panel'),
          searchParams.get('broadband-min'),
          searchParams.get('broadband-max'),
          hoveredCountyId,
        ],
      },
    });

    setLayer(countyBroadbandCompositeLayer);
    setPreviousActive(active);
    setPreviousActiveCounty(searchParams.get('active-county'));
    setPreviousActiveCompareCounty(searchParams.get('active-compare-county'));
    setPreviousSocioeconomicFactor(searchParams.get('socioeconomic-factor'));
    setPreviousYear(searchParams.get('year'));
    setPreviousHoveredCountyId(hoveredCountyId);
    setPreviousActivePanel(searchParams.get('active-panel'));
    setPreviousBroadbandMin(searchParams.get('broadband-min'));
    setPreviousBroadbandMax(searchParams.get('broadband-max'));
  }, [
    active,
    cartoLayerProps,
    countyBroadbandLayer,
    hoveredCountyId,
    layer,
    previousActive,
    previousActiveCompareCounty,
    previousActiveCounty,
    previousActivePanel,
    previousBroadbandMax,
    previousBroadbandMin,
    previousHoveredCountyId,
    previousSocioeconomicFactor,
    previousYear,
    searchParams,
    setSearchParams,
    source,
    viewState.zoom,
  ]);

  return layer;
}
